class SuiteProductReviews extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready === "true") return;

    this.dataset.ready = "true";
    this.reviewsUrl = this.dataset.reviewsUrl;
    this.submitUrl = this.dataset.submitUrl;
    this.productId = this.dataset.productId;
    this.productHandle = this.dataset.productHandle;
    this.productTitle = this.dataset.productTitle;
    this.pageSize = Math.max(Number(this.dataset.reviewsPerPage) || 5, 1);
    this.page = 1;
    this.reviews = [];

    this.summaryTarget = this.querySelector("[data-suite-reviews-summary]");
    this.listTarget = this.querySelector("[data-suite-reviews-list]");
    this.form = this.querySelector("[data-suite-reviews-form]");
    this.formPanel = this.querySelector("[data-suite-review-form-panel]");
    this.statusTarget = this.querySelector("[data-suite-reviews-status]");
    this.sortSelect = this.querySelector("[data-suite-reviews-sort]");
    this.paginationWrap = this.querySelector("[data-suite-pagination]");
    this.pagePrev = this.querySelector("[data-suite-page-prev]");
    this.pageNext = this.querySelector("[data-suite-page-next]");
    this.pageCurrent = this.querySelector("[data-suite-page-current]");
    this.lightbox = this.querySelector("[data-suite-lightbox]");
    this.lightboxImage = this.querySelector("[data-suite-lightbox-image]");
    this.lightboxClose = this.querySelector("[data-suite-lightbox-close]");
    this.reviewToggle = this.querySelector("[data-suite-review-toggle]");
    this.reviewClose = this.querySelector("[data-suite-review-close]");

    this.form?.addEventListener("submit", this.handleSubmit);
    this.sortSelect?.addEventListener("change", this.handleSort);
    this.pagePrev?.addEventListener("click", this.handlePrevPage);
    this.pageNext?.addEventListener("click", this.handleNextPage);
    this.listTarget?.addEventListener("click", this.handleMediaClick);
    this.lightboxClose?.addEventListener("click", this.closeLightbox);
    this.lightbox?.addEventListener("click", this.handleLightboxBackdrop);
    this.reviewToggle?.addEventListener("click", this.openForm);
    this.reviewClose?.addEventListener("click", this.closeForm);

    this.prefillOrderReference();
    this.prepareReviewLoading();
  }

  disconnectedCallback() {
    this.form?.removeEventListener("submit", this.handleSubmit);
    this.sortSelect?.removeEventListener("change", this.handleSort);
    this.pagePrev?.removeEventListener("click", this.handlePrevPage);
    this.pageNext?.removeEventListener("click", this.handleNextPage);
    this.listTarget?.removeEventListener("click", this.handleMediaClick);
    this.lightboxClose?.removeEventListener("click", this.closeLightbox);
    this.lightbox?.removeEventListener("click", this.handleLightboxBackdrop);
    this.reviewToggle?.removeEventListener("click", this.openForm);
    this.reviewClose?.removeEventListener("click", this.closeForm);
    this.reviewObserver?.disconnect();
  }

  prepareReviewLoading() {
    const shouldDefer = this.dataset.deferLoading === "true";
    if (!shouldDefer || !("IntersectionObserver" in window)) {
      this.loadReviews();
      return;
    }

    this.reviewObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.reviewObserver.disconnect();
        this.loadReviews();
      },
      { rootMargin: "600px 0px" },
    );
    this.reviewObserver.observe(this);
  }

  async loadReviews() {
    if (!this.reviewsUrl) {
      this.renderLoadError("Connect the Sharkflow app proxy to load reviews.");
      return;
    }

    try {
      const response = await fetch(this.reviewsUrl, {
        credentials: "same-origin",
        headers: { Accept: "application/json" },
      });
      if (!response.ok) throw new Error("Reviews could not be loaded.");

      this.applyPayload(await response.json());
    } catch (error) {
      this.renderLoadError(error.message || "Reviews could not be loaded.");
    }
  }

  applyPayload(payload) {
    this.reviews = Array.isArray(payload?.reviews) ? payload.reviews : [];
    this.visibleCount = this.pageSize;
    this.querySelectorAll("[data-suite-dynamic-summary]").forEach((element) => {
      element.hidden = false;
    });
    this.renderSummary(payload?.summary || {}, this.reviews);
    this.renderReviews();
  }

  renderSummary(summary, reviews) {
    if (!this.summaryTarget) return;

    // Shopify's reviews.rating metafield is the authoritative aggregate; the app
    // supplies the individual review texts. If the app returns no records --
    // e.g. history not yet imported -- keep the Shopify summary rather than
    // replacing a real score with 0.0.
    const initialAverage = Number(this.dataset.initialAverage || 0);
    const initialCount = Number(this.dataset.initialCount || 0);

    let average = Number(
      summary?.averageRating ?? Number(summary?.averageRatingX100 || 0) / 100,
    );
    let count = Number(summary?.reviewCount || reviews.length || 0);

    let usingShopifySummary = false;
    if (count === 0 && initialCount > 0) {
      average = initialAverage;
      count = initialCount;
      usingShopifySummary = true;
    }
    const counts = this.ratingCounts(summary?.ratingCounts, reviews);
    const positiveCount = Number(counts[5] || 0) + Number(counts[4] || 0);
    const recommendation = count > 0 ? Math.round((positiveCount / count) * 100) : 0;
    const countText = (this.dataset.countLabel || "{count} reviews").replace(
      "{count}",
      count,
    );

    this.summaryTarget.querySelector("[data-suite-average]").textContent =
      average > 0 ? average.toFixed(1) : "0.0";
    this.summaryTarget.querySelector("[data-suite-count]").textContent = countText;
    this.querySelector("[data-suite-toolbar-count]").textContent = countText;

    const starsTarget = this.summaryTarget.querySelector("[data-suite-stars]");
    if (starsTarget) {
      starsTarget.innerHTML = this.starsMarkup(Math.round(average));
      starsTarget.setAttribute("aria-label", `${average.toFixed(1)} out of 5 stars`);
    }

    const breakdown = this.summaryTarget.querySelector("[data-suite-rating-breakdown]");
    if (breakdown) breakdown.hidden = usingShopifySummary;

    for (let rating = 5; rating >= 1; rating -= 1) {
      const row = this.summaryTarget.querySelector(
        `[data-suite-rating-row="${rating}"]`,
      );
      if (!row) continue;

      const ratingCount = Number(counts[rating] || 0);
      const percentage = count > 0 ? Math.round((ratingCount / count) * 100) : 0;
      row.querySelector("[data-suite-rating-fill]").style.width = `${percentage}%`;
      row.querySelector("[data-suite-rating-count]").textContent = ratingCount;
      row
        .querySelector("[role='progressbar']")
        .setAttribute("aria-valuenow", String(percentage));
    }

    const recommendationValue = this.summaryTarget.querySelector(
      "[data-suite-recommendation-value]",
    );
    const recommendationCopy = this.summaryTarget.querySelector(
      "[data-suite-recommendation-copy]",
    );
    const recommendationBlock = this.summaryTarget.querySelector(
      "[data-suite-recommendation]",
    );
    if (recommendationBlock) recommendationBlock.hidden = usingShopifySummary;
    if (recommendationValue) recommendationValue.textContent = `${recommendation}%`;
    if (recommendationCopy) {
      recommendationCopy.textContent = (
        this.dataset.recommendationLabel || "{percent}% would recommend this product"
      ).replace("{percent}", recommendation);
    }
  }

  ratingCounts(rawCounts, reviews) {
    const counts = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };

    if (rawCounts && typeof rawCounts === "object") {
      for (let rating = 1; rating <= 5; rating += 1) {
        counts[rating] = Number(rawCounts[rating] || 0);
      }
      return counts;
    }

    reviews.forEach((review) => {
      const value = this.rating(review.rating);
      if (value > 0) counts[value] += 1;
    });
    return counts;
  }

  renderReviews() {
    if (!this.listTarget) return;

    const reviews = this.sortedReviews();
    const totalPages = Math.max(Math.ceil(reviews.length / this.pageSize), 1);
    this.page = Math.min(Math.max(this.page, 1), totalPages);
    const start = (this.page - 1) * this.pageSize;
    const visibleReviews = reviews.slice(start, start + this.pageSize);
    this.listTarget.setAttribute("aria-busy", "false");

    if (!reviews.length) {
      this.listTarget.innerHTML = `<p class="suite-reviews__empty">${this.escape(this.dataset.emptyText)}</p>`;
    } else {
      this.listTarget.innerHTML = visibleReviews
        .map((review) => this.reviewMarkup(review))
        .join("");
    }

    if (this.paginationWrap) {
      this.paginationWrap.hidden = totalPages <= 1;
      if (this.pageCurrent) this.pageCurrent.textContent = String(this.page);
      if (this.pagePrev) this.pagePrev.disabled = this.page <= 1;
      if (this.pageNext) this.pageNext.disabled = this.page >= totalPages;
    }
  }

  sortedReviews() {
    const reviews = [...this.reviews];
    const sort = this.sortSelect?.value || "recent";

    if (sort === "rating-high") {
      return reviews.sort((left, right) => this.rating(right.rating) - this.rating(left.rating));
    }
    if (sort === "rating-low") {
      return reviews.sort((left, right) => this.rating(left.rating) - this.rating(right.rating));
    }

    return reviews.sort((left, right) => {
      const leftDate = Date.parse(left.publishedAt || "") || 0;
      const rightDate = Date.parse(right.publishedAt || "") || 0;
      return rightDate - leftDate;
    });
  }

  reviewMarkup(review) {
    const rating = this.rating(review.rating);
    const date = review.publishedAt && !Number.isNaN(Date.parse(review.publishedAt))
      ? new Intl.DateTimeFormat(undefined, { dateStyle: "medium" }).format(
          new Date(review.publishedAt),
        )
      : "";
    const customerName = review.customerName || "Customer";
    const mediaMarkup = this.reviewMediaMarkup(review, customerName);

    return `
      <article class="suite-reviews__item">
        <div class="suite-reviews__item-aside">
          <strong>${this.escape(customerName)}</strong>
          ${review.verifiedPurchase ? `<span class="suite-reviews__badge">${this.escape(this.dataset.verifiedLabel || "Verified purchase")}</span>` : ""}
          ${date ? `<p class="suite-reviews__meta">${this.escape(date)}</p>` : ""}
        </div>
        <div class="suite-reviews__item-content">
          <span class="suite-reviews__stars" aria-label="${rating} out of 5 stars">${this.starsMarkup(rating)}</span>
          ${review.title ? `<h3 class="suite-reviews__item-title">${this.escape(review.title)}</h3>` : ""}
          <p class="suite-reviews__body">${this.escape(review.body)}</p>
          ${mediaMarkup}
        </div>
      </article>
    `;
  }

  reviewMediaMarkup(review, customerName) {
    if (!Array.isArray(review.media)) return "";

    const images = review.media
      .filter((media) => media?.kind === "image" && media.url)
      .slice(0, 4);
    if (!images.length) return "";

    const items = images.map((media, index) => {
      const fallbackAlt = `Photo ${index + 1} from ${customerName}'s review`;
      const alt = String(media.alt || "").trim() || fallbackAlt;
      const label = `Open ${fallbackAlt.toLowerCase()}`;

      return `
        <a class="suite-reviews__media-link" href="${this.escape(media.url)}" target="_blank" rel="noopener noreferrer" aria-label="${this.escape(label)}">
          <img class="suite-reviews__media-image" src="${this.escape(media.url)}" alt="${this.escape(alt)}" loading="lazy" decoding="async" width="640" height="640">
        </a>
      `;
    }).join("");

    return `<div class="suite-reviews__media" aria-label="Review photos">${items}</div>`;
  }

  starsMarkup(rating) {
    const filled = this.rating(rating);
    const symbol = this.escape(this.dataset.starSymbol);

    return Array.from({ length: 5 }, (_, index) => `
      <svg class="suite-reviews__star${index < filled ? " is-filled" : ""}" viewBox="0 0 20 20" aria-hidden="true" focusable="false">
        <use href="#${symbol}"></use>
      </svg>
    `).join("");
  }

  handleSort = () => {
    this.page = 1;
    this.renderReviews();
  };

  handlePrevPage = () => {
    this.page -= 1;
    this.renderReviews();
    this.listTarget?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  handleNextPage = () => {
    this.page += 1;
    this.renderReviews();
    this.listTarget?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  handleMediaClick = (event) => {
    const link = event.target.closest(".suite-reviews__media-link");
    if (!link || !this.lightbox || !this.lightboxImage) return;
    event.preventDefault();
    const image = link.querySelector("img");
    this.lightboxImage.src = link.href;
    this.lightboxImage.alt = image?.alt || "";
    this.lightbox.showModal();
  };

  closeLightbox = () => {
    this.lightbox?.close();
  };

  handleLightboxBackdrop = (event) => {
    if (event.target === this.lightbox) this.lightbox.close();
  };

  openForm = () => {
    if (!this.formPanel) return;
    this.formPanel.hidden = false;
    this.reviewToggle?.setAttribute("aria-expanded", "true");
    this.formPanel.querySelector("input:checked, input, textarea")?.focus();
  };

  closeForm = () => {
    if (!this.formPanel) return;
    this.formPanel.hidden = true;
    this.reviewToggle?.setAttribute("aria-expanded", "false");
    this.reviewToggle?.focus();
  };

  handleSubmit = async (event) => {
    event.preventDefault();
    if (!this.form || !this.submitUrl) return;

    const formData = new FormData(this.form);
    const fields = this.form.querySelectorAll("button, input, textarea");
    this.setStatus("Submitting review...");
    fields.forEach((field) => { field.disabled = true; });

    try {
      const response = await fetch(this.submitUrl, {
        method: "POST",
        credentials: "same-origin",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        body: JSON.stringify({
          shopifyProductId: this.productId,
          shopifyProductHandle: this.productHandle,
          shopifyProductTitle: this.productTitle,
          shopifyOrderName: String(formData.get("shopifyOrderName") || "").trim() || undefined,
          rating: Number(formData.get("rating")),
          title: String(formData.get("title") || "").trim(),
          body: String(formData.get("body") || "").trim(),
        }),
      });

      const payload = await response.json().catch(() => ({}));
      if (response.status === 401 || response.status === 403) {
        throw new Error("Please sign in to submit a review.");
      }
      if (!response.ok || payload.ok === false) {
        throw new Error(payload.message || payload.error || "Review was not submitted.");
      }

      this.form.reset();
      this.setStatus(
        payload.message || "Review submitted. It will appear after moderation.",
        "success",
      );
    } catch (error) {
      this.setStatus(error.message || "Review was not submitted.", "error");
    } finally {
      fields.forEach((field) => { field.disabled = false; });
    }
  };

  prefillOrderReference() {
    if (!this.form) return;
    const orderName = new URLSearchParams(window.location.search).get("review_order");
    const orderInput = this.form.querySelector('[name="shopifyOrderName"]');
    if (orderName && orderInput && !orderInput.value) orderInput.value = orderName;
  }

  renderLoadError(message) {
    if (!this.listTarget) return;
    this.listTarget.setAttribute("aria-busy", "false");
    this.listTarget.innerHTML = `<p class="suite-reviews__status" data-state="error">${this.escape(message)}</p>`;
  }

  setStatus(message, state = "message") {
    if (!this.statusTarget) return;
    this.statusTarget.textContent = message;
    this.statusTarget.dataset.state = state;
  }

  rating(value) {
    return Math.max(0, Math.min(5, Math.round(Number(value) || 0)));
  }

  escape(value) {
    return String(value || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

}

if (!customElements.get("suite-product-reviews")) {
  customElements.define("suite-product-reviews", SuiteProductReviews);
}
